/**
 * Admin › Export — DESIGN.md §16.5 (R44).
 *
 * "Slate exists because Touchline is escaping one lock-in; it MUST never create
 * another."
 *
 * So this screen's job is to make leaving *easy and obvious*, not to bury it.
 * It shows exactly what an archive contains before you download it, and says
 * what the archive is for: the disaster-recovery format of last resort, and the
 * way out if Slate ever stops being the right answer.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Download, HardDriveDownload } from 'lucide-react';
import { toast } from 'sonner';
import { CLIENT_HEADER, CLIENT_HEADER_VALUE } from '@slate/shared';
import { Button } from '@/components/ui/button';
import { LoadingRegion } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { API_BASE, http } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/admin/export')({
  component: ExportPage,
});

interface ExportSummary {
  version: number;
  generatedAt: string;
  counts: Record<string, number>;
  files: number;
  fileBytes: number;
  redacted: Record<string, string[]>;
}

function ExportPage(): JSX.Element {
  const summary = useQuery({
    queryKey: ['admin', 'export'],
    queryFn: () => http.get<ExportSummary>('/admin/export'),
  });
  const [downloading, setDownloading] = useState(false);
  const [includeFiles, setIncludeFiles] = useState(true);

  const download = async (): Promise<void> => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', [CLIENT_HEADER]: CLIENT_HEADER_VALUE },
        body: JSON.stringify({ includeFiles }),
      });
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
        'slate-export.tar';
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  if (summary.isLoading) return <LoadingRegion label="Counting rows" />;
  if (summary.isError) {
    return <p className="text-destructive text-sm">{summary.error.message}</p>;
  }

  const data = summary.data!;
  const rows = Object.entries(data.counts).filter(([, count]) => count > 0);
  const total = rows.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div className="space-y-5">
      <div className="max-w-2xl space-y-2 text-sm">
        <p className="text-muted-foreground">
          A complete archive of this workspace: every table as JSON with its ids intact, plus every
          attachment’s bytes. It is the disaster-recovery format of last resort, and it is the way
          out — Slate replaced one system Touchline was locked into, and it must never become
          another.
        </p>
        <p className="text-muted-foreground">
          The same archive is available from a shell as <code>pnpm export:all</code>, which is the
          form that still works when the web app does not.
        </p>
      </div>

      <div className="bg-card space-y-4 rounded-lg border p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">
              {total.toLocaleString()} rows across {rows.length} tables
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {data.files.toLocaleString()} attachment{data.files === 1 ? '' : 's'} (
              {megabytes(data.fileBytes)})
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeFiles}
                onChange={(event) => setIncludeFiles(event.target.checked)}
              />
              Include attachment files
            </label>
            <Button onClick={() => void download()} disabled={downloading}>
              {downloading ? <HardDriveDownload /> : <Download />}
              {downloading ? 'Building the archive…' : 'Download archive'}
            </Button>
          </div>
        </div>

        <p className="text-muted-foreground text-xs">
          Password hashes and token hashes are not included ({Object.keys(data.redacted).join(', ')}
          ) — an archive of the workspace should not be an archive of everyone’s credentials. The
          manifest inside says so, so whoever restores it knows why nobody can sign in.
        </p>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Table</TableHead>
              <TableHead className="text-right">Rows</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([table, count]) => (
              <TableRow key={table}>
                <TableCell className="font-mono text-xs">{table}</TableCell>
                <TableCell className="text-right tabular-nums">{count.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function megabytes(bytes: number): string {
  if (bytes === 0) return 'no files';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
